import { useState, useEffect, useCallback } from 'react';
import { useErrorHandler } from '../../utils/useErrorHandler';
import { User } from '../../user/types/type';
import dataProvider from '../../services/api';
import { TRANSACTION_MANAGEMENT_CONSTANTS } from '../constants/transactionManagement';

export const useUsers = () => {
  const { handleErrorWithLogout } = useErrorHandler();

  const [users, setUsers] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalUsers, setTotalUsers] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [usersPerPage, setUsersPerPage] = useState<number>(
    TRANSACTION_MANAGEMENT_CONSTANTS.DEFAULT_USERS_PER_PAGE
  );

  const handleUsersPerPageChange = useCallback((newUsersPerPage: number) => {
    if (newUsersPerPage !== usersPerPage) {
      setUsersPerPage(newUsersPerPage);
      setCurrentPage(1); 
    }
  }, [usersPerPage]);

  const parseSearchTerm = (term: string) => {
    const cleanTerm = term.trim().toLowerCase();
    const words = cleanTerm.split(/\s+/).filter(word => word.length > 0);
    
    return {
      originalTerm: cleanTerm,
      words: words,
      firstName: words[0] || '',
      lastName: words[words.length - 1] || '',
      middleName: words.length > 2 ? words.slice(1, -1).join(' ') : ''
    };
  };

  const searchWithKeyword = async (keyword: string) => {
    try {
      const { data } = await dataProvider.getListUsers({
        filter: keyword,
        page: 0,
        perPage: TRANSACTION_MANAGEMENT_CONSTANTS.SEARCH_RESULTS_LIMIT, 
      });
      return data;
    } catch (error) {
      handleErrorWithLogout(error);
      return [];
    }
  };

  const isUserMatchSearchTerm = (user: User, searchTerm: string) => {
    const fullName = `${user.firstName} ${user.middleName || ''} ${user.lastName}`.trim().toLowerCase();
    const searchLower = searchTerm.toLowerCase();

    if (fullName.includes(searchLower)) return true;

    const searchWords = searchLower.split(/\s+/);
    const nameWords = fullName.split(/\s+/);

    return searchWords.every(searchWord => 
      nameWords.some(nameWord => 
        nameWord.includes(searchWord) || searchWord.includes(nameWord)
      )
    );
  };

  const calculateRelevanceScore = (user: User, searchTerm: string) => {
    const fullName = `${user.firstName} ${user.middleName || ''} ${user.lastName}`.trim().toLowerCase();
    const searchLower = searchTerm.toLowerCase();
    
    let score = 0;
    
    // Exact match scores
    if (fullName === searchLower) score += 100;
    
    // Partial match scores
    if (fullName.includes(searchLower)) score += 50;
    
    const searchWords = searchLower.split(/\s+/);
    const nameWords = fullName.split(/\s+/);
    
    searchWords.forEach(searchWord => {
      nameWords.forEach(nameWord => {
        if (nameWord === searchWord) score += 30;
        else if (nameWord.includes(searchWord)) score += 20;
        else if (searchWord.includes(nameWord)) score += 10;
      });
    });
    
    return score;
  };

  const smartSearch = async (searchTerm: string) => {
    if (!searchTerm.trim()) {
      const totalData = await dataProvider.userTotalCount({ filter: '', count: usersPerPage });
      const totalUsers = totalData.data.count;

      const { data } = await dataProvider.getListUsers({
        filter: '',
        page: currentPage - 1,
        perPage: usersPerPage,
      });

      return { users: data, total: totalUsers };
    }

    const parsed = parseSearchTerm(searchTerm);
    let allUsers: User[] = [];
    
    const searchPromises = [];
    
    if (parsed.firstName) {
      searchPromises.push(searchWithKeyword(parsed.firstName));
    }
    
    if (parsed.lastName && parsed.lastName !== parsed.firstName) {
      searchPromises.push(searchWithKeyword(parsed.lastName));
    }
    
    if (parsed.middleName) {
      searchPromises.push(searchWithKeyword(parsed.middleName));
    }
    
    searchPromises.push(searchWithKeyword(searchTerm));

    const results = await Promise.all(searchPromises);
    
    results.forEach(result => {
      allUsers = allUsers.concat(result);
    });

    const uniqueUsers = allUsers.filter((user, index, self) => 
      index === self.findIndex(u => u.userId === user.userId)
    );

    const matchedUsers = uniqueUsers
      .filter(user => isUserMatchSearchTerm(user, searchTerm))
      .map(user => ({
        ...user,
        relevanceScore: calculateRelevanceScore(user, searchTerm)
      }))
      .sort((a, b) => b.relevanceScore - a.relevanceScore);

    const startIndex = (currentPage - 1) * usersPerPage;
    const endIndex = startIndex + usersPerPage;
    const paginatedUsers = matchedUsers.slice(startIndex, endIndex);

    return {
      users: paginatedUsers,
      total: matchedUsers.length
    };
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const result = await smartSearch(searchTerm);

      const mappedUsers = result.users.map((user: User) => ({
        ...user,
        name: `${user.firstName} ${user.middleName || ''} ${user.lastName}`.trim(),
        middleName: user.middleName || '',
        profileImageUrl: user.profileImageUrl ? 
          `${user.profileImageUrl}?t=${new Date().getTime()}` : '',
      }));

      setUsers(mappedUsers);
      setTotalUsers(result.total);

      if (mappedUsers.length > 0 && !selectedUser) {
        setSelectedUser(mappedUsers[0]);
      } else if (selectedUser) {
        const updated = mappedUsers.find((u: User) => u.userId === selectedUser.userId);
        if (updated) setSelectedUser(updated);
      }
    } catch (error: any) {
      handleErrorWithLogout(error);
      setUsers([]);
      setTotalUsers(0);
      setSelectedUser(null);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(Math.max(1, Math.min(page, Math.ceil(totalUsers / usersPerPage))));
  };

  const handleUserSelect = (user: User) => {
    setSelectedUser(user);
  };

  useEffect(() => {
    fetchUsers();
  }, [currentPage, searchTerm, usersPerPage]); 

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  return {
    users,
    selectedUser,
    searchTerm,
    setSearchTerm,
    currentPage,
    totalUsers,
    usersPerPage,
    loading,
    handlePageChange,
    handleUserSelect,
    fetchUsers,
    onUsersPerPageChange: handleUsersPerPageChange, // Export callback
  };
};